<!--
  Every block below is rendered on its own condition. Nothing here is gated on "there is
  editorial content": a spirit with a sensory profile and no prose renders a profile, and a
  spirit with prose and no assessment renders prose, with no empty scaffolding in either case.

  The accent variable is set from the dominant character group, so the sheet takes the colour of
  the spirit rather than always the brand red. With no character published it falls back to the
  SpiritsRim ember, which is why every consumer of it declares a fallback too.
-->
<div class="editorial" [style.--editorial-accent]="accent()">

    <!-- Storytelling: the voice of the sheet, tinted by the spirit's own family. -->
    @if (storytelling().length > 0) {
        <section class="editorial-statement" [attr.aria-label]="'editorial.narrative' | translate">
            @for (paragraph of storytelling(); track $index) {
                <p>{{ paragraph }}</p>
            }
        </section>
    }

    <!--
      Map of the sheet, right after the opening: before scrolling, the guest sees what this
      bottle actually has. It is built from the sections that rendered, so no link can miss, and
      it only appears on a sheet long enough to get lost in.
    -->
    @if (showSectionIndex()) {
        <div class="editorial-index-bar" [class.fade-start]="fadeStart()" [class.fade-end]="fadeEnd()">
            <nav #indexNav class="editorial-index" [attr.aria-label]="'editorial.sections' | translate"
                 (scroll)="updateFades()" (touchmove)="noteManualDrag()" (wheel)="noteManualDrag()">
                @for (link of sectionLinks(); track link.id) {
                    <a [href]="'#' + link.id" [class.active]="link.id === activeId()"
                       [attr.aria-current]="link.id === activeId() ? 'location' : null"
                       (click)="scrollToSection($event, link.id)">{{ link.label }}</a>
                }
            </nav>
        </div>
    }

    <div class="editorial-info-section">
        <div class="editorial-info-blocks">

            <!-- Character and sensory profile lead: they are the fastest read of the sheet. -->
            @if (tags().length > 0) {
                <section class="editorial-block">
                    <h3 class="editorial-heading" [id]="anchorOf('character')">
                        <app-editorial-icon section="character"></app-editorial-icon>
                        {{ 'editorial.character' | translate }}
                    </h3>
                    <app-spirit-character-tags [tags]="tags()"></app-spirit-character-tags>
                </section>
            }

            @if (sensory().length > 0) {
                <section class="editorial-block">
                    <h3 class="editorial-heading" [id]="anchorOf('sensory')">
                        <app-editorial-icon section="sensory"></app-editorial-icon>
                        {{ 'editorial.sensory' | translate }}
                    </h3>
                    <app-spirit-sensory-profile [axes]="sensory()"></app-spirit-sensory-profile>
                </section>
            }

            <!--
              Tasting notes as a track, not as tabs. Tabs hid three of the four stages behind a
              click; the sequence sight -> nose -> palate -> finish IS the order of the act, so
              it is rendered whole.
            -->
            @if (tastingSteps().length > 0) {
                <section class="editorial-block">
                    <h3 class="editorial-heading" [id]="anchorOf('tasting')">
                        <app-editorial-icon section="tasting"></app-editorial-icon>
                        {{ 'editorial.tasting_note' | translate }}
                    </h3>
                    <ol class="tasting-track">
                        @for (step of tastingSteps(); track step.field) {
                            <li>
                                <h4>{{ step.label | translate }}</h4>
                                <p>{{ step.text }}</p>
                            </li>
                        }
                    </ol>
                </section>
            }

            @if (productionEntries().length > 0) {
                <section class="editorial-block">
                    <h3 class="editorial-heading" [id]="anchorOf('production')">
                        <app-editorial-icon section="production"></app-editorial-icon>
                        {{ 'editorial.production' | translate }}
                    </h3>
                    <dl class="production-slabs">
                        @for (entry of productionEntries(); track entry.field) {
                            <div class="slab">
                                <dt>{{ entry.label | translate }}</dt>
                                <dd>{{ entry.text }}</dd>
                            </div>
                        }
                    </dl>
                </section>
            }

            @if (ingredients(); as ingredientsText) {
                <section class="editorial-block">
                    <h3 class="editorial-heading" [id]="anchorOf('ingredients')">
                        <app-editorial-icon section="ingredients"></app-editorial-icon>
                        {{ 'editorial.ingredients' | translate }}
                    </h3>
                    <p class="editorial-prose">{{ ingredientsText }}</p>
                </section>
            }

            @if (affinities().length > 0) {
                <section class="editorial-block">
                    <h3 class="editorial-heading" [id]="anchorOf('affinities')">
                        <app-editorial-icon section="affinities"></app-editorial-icon>
                        {{ 'editorial.affinities' | translate }}
                    </h3>
                    <app-spirit-affinities [affinities]="affinities()"></app-spirit-affinities>
                </section>
            }

            <!--
              "Product information", not "verified data": the guest reads facts about a bottle,
              not a report about our pipeline.

              Catalogue identity and declared facts sit in one section because a guest asking
              "what is this bottle" wants both, and in two separate lists because they are not
              the same kind of statement: a catalogue row is a datum the venue already had, a
              fact is a tri-state claim with its own verified/unknown wording.
            -->
            @if (hasProductInformation()) {
                <section class="editorial-block">
                    <h3 class="editorial-heading" [id]="anchorOf('facts')">
                        <app-editorial-icon section="facts"></app-editorial-icon>
                        {{ 'editorial.facts' | translate }}
                    </h3>

                    @if (catalogRows().length > 0) {
                        <dl class="catalog-specs">
                            @for (row of catalogRows(); track row.key) {
                                <div class="spec">
                                    <dt>{{ row.label | translate }}</dt>
                                    <dd>{{ row.value | catalogTranslation }}</dd>
                                </div>
                            }
                        </dl>
                    }

                    @if (facts().length > 0) {
                        <app-spirit-product-facts [facts]="facts()"></app-spirit-product-facts>
                    }
                </section>
            }

            @if (awards().length > 0) {
                <section class="editorial-block">
                    <h3 class="editorial-heading" [id]="anchorOf('awards')">
                        <app-editorial-icon section="awards"></app-editorial-icon>
                        {{ 'editorial.awards' | translate }}
                    </h3>
                    <app-spirit-awards [awards]="awards()"></app-spirit-awards>
                </section>
            }

        </div>
    </div>

</div>
